import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/db7t4mv3hiaolq192lh0";

const contentScope = [
  { icon: "ri-movie-2-line", title: "电影档案", desc: "收录香港三级片代表作品的导演、主演、年份、类型与剧情资料。" },
  { icon: "ri-layout-grid-line", title: "分类导航", desc: "按年代、类型、导演与评分等维度组织内容，便于系统浏览。" },
  { icon: "ri-bookmark-line", title: "本地收藏", desc: "收藏记录仅保存在当前浏览器，不涉及账号与云端数据。" },
  { icon: "ri-file-text-line", title: "剧情解析", desc: "以影评视角梳理作品的主题、创作背景与类型特征。" },
];

export default function AboutPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(String(serverMsg) || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后再试。");
    }
  };

  return (
    <div className="w-full px-4 py-8 md:px-6 md:py-10">
      <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
        <Link to="/" className="cursor-pointer transition-colors hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line text-foreground-300" />
        <span className="text-foreground-800">关于</span>
      </nav>

      <header className="mt-6 overflow-hidden rounded-lg border border-background-300/70 bg-background-50">
        <div className="relative h-48 w-full md:h-60">
          <img
            src="https://readdy.ai/api/search-image?query=vintage%20cinema%20hall%20with%20red%20velvet%20seats%20and%20warm%20projector%20light%20beam%2C%20crimson%20and%20amber%20tones%2C%20nostalgic%20film%20archive%20atmosphere%2C%20soft%20grain%2C%20artistic%20wide%20shot%2C%20no%20people&width=1600&height=600&seq=about-banner-01&orientation=landscape"
            alt="电影放映厅"
            title="香港三级片电影资料站"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/85 to-primary-950/30" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
            <h1 className="font-heading text-2xl font-bold text-background-50 md:text-4xl">关于本站</h1>
            <p className="mt-2 max-w-2xl text-sm text-background-100/85 md:text-base">
              香港三级片 · 影视资料站 —— 一座专注于该类型电影的资料档案库
            </p>
          </div>
        </div>
      </header>

      <section className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="flex flex-col gap-8 lg:col-span-2">
          <div className="rounded-lg border border-background-300/70 bg-background-50 p-6 md:p-8">
            <h2 className="font-heading text-xl font-bold text-foreground-950">网站定位与内容范围</h2>
            <p className="mt-3 text-sm leading-7 text-foreground-700">
              本站以「电影档案」的专业视角，聚焦香港三级片（Category III）这一特殊的电影类型，系统整理其代表作品的
              导演、主演、年代、类型、剧情梗概与相关标签，并通过多维度分类与检索功能，帮助影迷与研究者理解该类型片的
              历史脉络、创作生态与艺术价值。
            </p>
            <p className="mt-3 text-sm leading-7 text-foreground-700">
              我们的目标用户包括该类型电影的研究者、影评爱好者、香港电影影迷以及相关行业从业者。所有内容均以资料展示与
              影评分析为目的，力求客观、严谨、便于查阅。
            </p>

            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {contentScope.map((item) => (
                <div key={item.title} className="flex gap-3 rounded-lg border border-background-300/70 bg-background-100 p-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className={`${item.icon} text-lg`} />
                  </span>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900">{item.title}</h3>
                    <p className="mt-1 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-background-300/70 bg-background-50 p-6 md:p-8">
            <h2 className="font-heading text-xl font-bold text-foreground-950">版权与免责声明</h2>
            <ul className="mt-4 flex flex-col gap-3 text-sm leading-7 text-foreground-700">
              <li className="flex gap-2">
                <i className="ri-shield-check-line mt-1 text-primary-500" />
                <span>本站为影视资料与影评分享平台，所有文字内容仅用于电影研究、影评分析与学习交流，禁止用于任何商业用途。</span>
              </li>
              <li className="flex gap-2">
                <i className="ri-shield-check-line mt-1 text-primary-500" />
                <span>本站不提供任何影片的在线播放、下载或资源分享功能，页面中的图片均为示意性配图。</span>
              </li>
              <li className="flex gap-2">
                <i className="ri-shield-check-line mt-1 text-primary-500" />
                <span>站内电影信息、评分与剧情概要均来源于公开资料整理，如有疏漏或版权异议，欢迎通过下方反馈表单与我们联系。</span>
              </li>
              <li className="flex gap-2">
                <i className="ri-shield-check-line mt-1 text-primary-500" />
                <span>本站内容仅面向成年观众，请自觉遵守所在地区的法律法规与影视分级制度。</span>
              </li>
            </ul>
          </div>
        </div>

        <aside className="flex flex-col gap-6">
          <div className="rounded-lg border border-background-300/70 bg-background-50 p-6">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-mail-send-line text-primary-500" />
              联系我们
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              对本站内容有任何建议、纠错或合作意向，欢迎通过反馈表单或邮件与我们取得联系。
            </p>
            <div className="mt-4 flex flex-col gap-3 text-sm">
              <span className="flex items-center gap-2 text-foreground-700">
                <i className="ri-mail-line text-foreground-400" />
                feedback@hk3archive.example
              </span>
              <span className="flex items-center gap-2 text-foreground-700">
                <i className="ri-time-line text-foreground-400" />
                回复时间：1–3 个工作日
              </span>
            </div>
          </div>

          <div id="feedback-form" className="rounded-lg border border-background-300/70 bg-background-50 p-6">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-chat-smile-2-line text-primary-500" />
              意见反馈
            </h2>
            <p className="mt-1.5 text-xs text-foreground-500">你的反馈将帮助我们不断完善电影档案</p>

            <form data-readdy-form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-3.5">
              <div>
                <label htmlFor="feedback-name" className="text-xs font-medium text-foreground-600">称呼</label>
                <input
                  id="feedback-name"
                  type="text"
                  name="name"
                  required
                  placeholder="请输入你的称呼"
                  className="mt-1.5 h-10 w-full rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>
              <div>
                <label htmlFor="feedback-email" className="text-xs font-medium text-foreground-600">邮箱</label>
                <input
                  id="feedback-email"
                  type="email"
                  name="email"
                  required
                  placeholder="用于接收回复"
                  className="mt-1.5 h-10 w-full rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>
              <div>
                <label htmlFor="feedback-type" className="text-xs font-medium text-foreground-600">反馈类型</label>
                <select
                  id="feedback-type"
                  name="feedback_type"
                  defaultValue="内容建议"
                  className="mt-1.5 h-10 w-full cursor-pointer rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-900 outline-none focus:border-primary-400"
                >
                  <option value="内容建议">内容建议</option>
                  <option value="信息纠错">信息纠错</option>
                  <option value="版权相关">版权相关</option>
                  <option value="合作洽谈">合作洽谈</option>
                </select>
              </div>
              <div>
                <label htmlFor="feedback-message" className="text-xs font-medium text-foreground-600">留言</label>
                <textarea
                  id="feedback-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  placeholder="请描述你的反馈内容（最多 500 字）"
                  className="mt-1.5 w-full resize-none rounded-md border border-background-300 bg-background-100 px-3 py-2 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>

              <input
                className="form-hp-field"
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                disabled={status === "submitting"}
                className="mt-1 inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <i className="ri-send-plane-line" />
                {status === "submitting" ? "提交中..." : "提交反馈"}
              </button>

              {status === "success" && (
                <p className="flex items-center gap-2 rounded-md bg-accent-100 px-3 py-2 text-xs text-accent-900">
                  <i className="ri-checkbox-circle-line" />
                  提交成功，感谢你的反馈！
                </p>
              )}
              {status === "error" && (
                <p className="flex items-center gap-2 rounded-md bg-primary-100 px-3 py-2 text-xs text-primary-700">
                  <i className="ri-error-warning-line" />
                  {formError}
                </p>
              )}
            </form>
          </div>
        </aside>
      </section>
    </div>
  );
}